/**
 * useKhorosUser hook helper
 * Interface Khoros User profile REST API
 *
 * Author: Relevant Bits
 */

import { useContext } from 'react';

import { useQuery } from '@tanstack/react-query';

import { ConfigContext } from '../../../aurr/contexts/ConfigContext';
import LoginContext from '../../../aurr/contexts/LoginContext';
import { cacheTTLs } from '../../../common/constants';
import { buildCacheKey, getAxiosInstance } from '../../axiosCache';
import { USER_QUERY_KEY } from '../constants';
import { KhorosUserProps } from '../interface/khoros.interface';
import { KhorosUserSchema } from '../type/khoros-user.type';

export const useKhorosUser = ({ userId, baseURL }: KhorosUserProps) => {
  const { KHOROS_API } = useContext(ConfigContext);
  const { access_token, isUserLoading, userProfile } = useContext(LoginContext);
  const sub = userProfile?.sub;

  // create axios client used in all community function requests
  const axiosConfig = {
    baseURL: baseURL || KHOROS_API,
    headers: {
      ...(access_token && { authorization: `Bearer ${access_token}` }),
      'Cache-Control': 'no-cache, max-age=0',
    },
    id: buildCacheKey([USER_QUERY_KEY, sub]),
    cache: {
      ttl: cacheTTLs.profile,
    },
  };
  const api = getAxiosInstance();

  const endpointPathname = userId ? 'user' : 'me';

  // Initially enable the query only if user data is loaded from cookies.
  let enabled = !isUserLoading;

  // Disable /me requests with no access_token present.
  if (enabled && !userId && !access_token) {
    enabled = false;
  }

  return useQuery<KhorosUserSchema>({
    queryKey: [USER_QUERY_KEY, { userId, sub }],
    queryFn: async () => {
      try {
        const response = await api.get(
          `/services/v2/community/khoros/${endpointPathname}`,
          axiosConfig
        );
        return response?.data || undefined;
      } catch (error) {
        return undefined;
      }
    },
    enabled,
  });
};
